import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import _ from 'lodash';

import AppContext from '../../context';
import { getPortfolioMeta } from '../../../api';
import ManageStepOne from './step_one/step_one.component';
import ManageStepTwo from './step_two/step_two.component';
import AddTx from '../add_transaction/add_tx.component';
import ViewEditTxs from './view_edit_txs/view_edit_txs.component';
import BrokerConnect from '../broker_connect/broker_connect.component';
import ConnectToBroker from './connect_to_broker/connect_to_broker.component';
import SwitchToBroker from './switch_mode/switch_to_broker.component';
import SwitchToManual from './switch_mode/switch_to_manual.component';
import EmptyPf from './empty_pf/empty_pf.component';
import ImportMFs from './import_mfs/import_mfs.component';
import KiteSyncCheck from './kite_sync_check/kite_sync_check.component';
import { onKiteSync, onBrokerSync } from '../../../utils';

import { DeletePf, RenamePfForm } from '../common/common';
import './manage_pf.component.css';

const ManagePf = (props) => {
  const context = useContext(AppContext);
  const {
    show,
    setShow,
    setContext,
    resetCurrentPfRef,
    managePfFrom,
    setManagePfFrom,
    showImportMFs,
    setShowImportMFs,
    setShowSyncBrokerMF,
  } = props;
  const [step, setStep] = useState('manage_one');
  const [currentPfId, setCurrentPfId] = useState('');
  const [currentPf, setCurrentPf] = useState(props.currentPf);
  const [currentPfSummary, setCurrentPfSummary] = useState(null);
  //on kite sync, prevent syncing same kite account to another pf
  const [isKitePfExist, setIsKitePfExist] = useState(false);

  const [showDeletePf, setShowDeletePf] = useState(false);
  const [showRenamePf, setShowRenamePf] = useState(false);

  //delete from step1 or 2 ref
  const deleteFromRef = useRef('manage_one');
  //add_tx from empty_pf or manage_two
  const addtxFromRef = useRef('manage_two');

  //android dep start
  const queryParams = useRef(null);
  const isWebview = useRef(null);
  //android dep end

  const getCurrentStep = () => {
    let comp;
    switch (step) {
      case 'manage_one':
        comp = (
          <ManageStepOne
            setStep={setStep}
            setCurrentPfId={setCurrentPfId}
            setShowDeletePf={setShowDeletePf}
            setDeleteFromRef={() => (deleteFromRef.current = 'manage_one')}
            setContext={setContext}
            onClose={() => setShow(false)}
          />
        );
        break;

      case 'manage_two':
        comp = (
          <ManageStepTwo
            pfId={currentPfId}
            setStep={setStep}
            currentPfSummary={currentPfSummary}
            setCurrentPfSummary={setCurrentPfSummary}
            onSync={() => setStep('import_summary')}
            setShowDeletePf={setShowDeletePf}
            setShowRenamePf={setShowRenamePf}
            setShowImportMFs={setShowImportMFs}
            setDeleteFromRef={() => (deleteFromRef.current = 'manage_two')}
            setAddtxFromRef={() => (addtxFromRef.current = 'manage_two')}
            onClose={() => setShow(false)}
          />
        );
        break;

      case 'addtx_one':
      case 'addtx_two':
        comp = (
          <AddTx
            pfId={currentPfId}
            step={step}
            setStep={setStep}
            onSave={
              isWebview.current
                ? window.Android.goBack.bind(Android)
                : context.portfoliosById[currentPfId].is_empty
                ? onFirstTimeAddtx
                : onHoldingsUpdate
            }
            addtxFromRef={addtxFromRef.current}
            isWebview={isWebview.current}
          />
        );
        break;

      case 'viewedittx':
        comp = (
          <ViewEditTxs
            pfId={currentPfId}
            setStep={setStep}
            summary={
              currentPfSummary
                ? _.flatten(
                    currentPfSummary
                      .filter((inst) => inst.name !== 'Mutual Funds')
                      .map((inst) => {
                        return inst.sub_rows;
                      }),
                  )
                : null
            }
            onSave={
              isWebview.current
                ? window.Android.goBack.bind(Android)
                : onHoldingsUpdate
            }
            isWebview={isWebview.current}
          />
        );
        break;

      case 'import_summary':
        comp = (
          <BrokerConnect
            pfId={currentPfId}
            setStep={setStep}
            onClose={onHoldingsUpdate}
            setContext={setContext}
            isWebview={isWebview.current}
          />
        );
        break;

      case 'broker_connect':
        comp = (
          <ConnectToBroker
            pfId={currentPfId}
            onBack={
              isWebview.current
                ? window.Android.goBack.bind(Android)
                : () => setStep('empty_pf')
            }
            onSync={
              isWebview.current
                ? window.Android.goBack.bind(Android)
                : onFirstTimeSync
            }
            onExistingKitePortfolioSync={onExistingKitePortfolioSync}
          />
        );
        break;

      case 'switch_to_broker':
        comp = (
          <SwitchToBroker
            pfId={currentPfId}
            onBack={() => setStep('manage_two')}
            setStep={setStep}
            setContext={setContext}
          />
        );
        break;

      case 'switch_to_manual':
        comp = (
          <SwitchToManual
            pfId={currentPfId}
            onBack={() => setStep('manage_two')}
            setStep={setStep}
            setContext={setContext}
          />
        );
        break;

      case 'empty_pf':
        comp = (
          <EmptyPf
            pfId={currentPfId}
            setStep={setStep}
            setAddtxFromRef={() => (addtxFromRef.current = 'empty_pf')}
            onClose={() => setShow(false)}
            onSync={onFirstTimeSync}
            setShowImportMFs={setShowImportMFs}
          />
        );
        break;

      default:
        break;
    }
    return comp;
  };

  const onExistingKitePortfolioSync = (pfId) => {
    setCurrentPfId(pfId);
    setIsKitePfExist(true);
  };

  const updatePfMeta = async () => {
    try {
      let pfs = _.cloneDeep(context.portfolios);
      const pfMeta = await getPortfolioMeta(currentPfId);
      const pfIdx = context.portfolios.findIndex(
        (_pf) => _pf.id == currentPfId,
      );
      pfs[pfIdx] = pfMeta.data;
      setContext({
        portfolios: pfs,
        resetPf: { resetId: currentPfId, switchId: currentPfId },
      });
    } catch (error) {
      window.location.reload();
    }
  };

  const onHoldingsUpdate = () => {
    updatePfMeta();
    setShow(false);
  };

  const onFirstTimeSync = async () => {
    updatePfMeta();
    setStep('import_summary');
  };

  const onFirstTimeAddtx = (has_equity, inception_date) => {
    updatePfMeta();
    setShow(false);
  };

  useEffect(() => {
    if (step === 'manage_one') setCurrentPfSummary(null);
    //show manage modal after sync with step import_summary
    if (managePfFrom === 'sync_from_summary' && step === 'import_summary') {
      setManagePfFrom('');
      setShowSyncBrokerMF(false);
    }
  }, [step]);

  useEffect(() => {
    // android dep start
    queryParams.current = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );
    isWebview.current = queryParams.current.user_agent === 'webview';
    const { user_agent, manage_step, pf_id, pf_type, broker } =
      queryParams.current;

    if (user_agent === 'webview' && pf_id && manage_step && pf_type) {
      if (!show) setShow(true);
      setCurrentPfId(pf_id);
      if (manage_step === 'add_tx') {
        if (pf_type === 'manual') {
          setStep('addtx_one');
          return;
        }
        if (pf_type === 'broker' && broker) {
          if (broker === 'kite') {
            onKiteSync(
              pf_id,
              () => setStep('import_summary'),
              onExistingKitePortfolioSync,
            );
          } else {
            onBrokerSync(pf_id, () => setStep('import_summary'));
          }
          return;
        }
      }
      if (manage_step === 'update_tx') {
        if (pf_type === 'manual') {
          setStep('viewedittx');
          return;
        }
        if (pf_type === 'broker' && broker) {
          if (broker === 'kite') {
            onKiteSync(
              pf_id,
              () => setStep('import_summary'),
              onExistingKitePortfolioSync,
            );
          } else {
            onBrokerSync(pf_id, () => setStep('import_summary'));
          }
          return;
        }
      }
      if (manage_step === 'convert') {
        if (pf_type === 'manual') {
          setStep('switch_to_broker');
          return;
        }
        if (pf_type === 'broker') {
          setStep('switch_to_manual');
          return;
        }
      }
    }
    // android dep end

    if (showImportMFs) {
      setStep('manage_one');
      return;
    }
    if (!show) {
      setStep('manage_one');
      setCurrentPfId('');
      resetCurrentPfRef();
    } else {
      if (props.currentPf) {
        setCurrentPfId(props.currentPf.id);
        if (managePfFrom === 'create_pf') {
          setStep('empty_pf');
          return;
        }
        if (managePfFrom === 'sync_from_summary') {
          const pf = props.currentPf;
          const { broker } = pf;
          if (broker.is_kite) {
            onKiteSync(
              props.currentPf.id,
              () => setStep('import_summary'),
              onExistingKitePortfolioSync,
            );
          } else {
            onBrokerSync(props.currentPf.id, () => setStep('import_summary'));
          }
          return;
        }
      }
    }
  }, [show]);

  useEffect(() => {
    if (showImportMFs && props.currentPf && !currentPfId) {
      setCurrentPfId(props.currentPf.id);
    }
    if (!showImportMFs && !show) {
      resetCurrentPfRef();
      setCurrentPfId('');
    }
  }, [showImportMFs]);

  useEffect(() => {
    setCurrentPf(context.portfoliosById[currentPfId]);
  }, [currentPfId]);

  return (
    <>
      <Modal
        show={show && managePfFrom != 'sync_from_summary' && !isKitePfExist}
        onHide={() => setShow(false)}
        centered={true}
        size="xl"
        animation={false}
        className={`manage-pf-modal add-tx-modal ${step}_step`}
      >
        <Modal.Body
          className={`p-0 ${
            ['addtx_one', 'addtx_two'].includes(step) ? 'add-tx-body' : ''
          }`}
        >
          {getCurrentStep()}
        </Modal.Body>
      </Modal>
      {currentPf && (
        <>
          <DeletePf
            pf={currentPf}
            show={showDeletePf}
            setShow={setShowDeletePf}
            setContext={setContext}
            onDelete={() => setStep('manage_one')}
            className="overlay"
          />
          <RenamePfForm
            pf={currentPf}
            show={showRenamePf}
            setShow={setShowRenamePf}
            setContext={setContext}
            className="overlay"
          />
        </>
      )}
      <ImportMFs
        pf={currentPf}
        show={showImportMFs}
        setShow={setShowImportMFs}
        setContext={setContext}
      />
      <KiteSyncCheck
        show={isKitePfExist}
        setShow={setIsKitePfExist}
        pf={context.portfoliosById[currentPfId]}
        onConfirm={() => {
          onKiteSync(
            currentPfId,
            () => {
              setStep('import_summary');
              setIsKitePfExist(false);
            },
            onExistingKitePortfolioSync,
          );
        }}
      />
    </>
  );
};

export default ManagePf;
